import { getScreenShake } from '@client/effects/ScreenShake';

export class GameLoop {
  private updateCallback: (deltaTime: number) => void;
  private renderCallback: () => void;
  private animationFrameId: number | null = null;
  private lastTime: number = 0;
  private deltaTime: number = 0;
  private fps: number = 0;
  private frameCount: number = 0;
  private fpsTime: number = 0;
  private errorState: { message: string; stack: string } | null = null;
  private onError: ((error: { message: string; stack: string }) => void) | null = null;

  // Fixed time step for physics/logic (60 updates per second)
  private readonly fixedDeltaTime: number = 1 / 60;
  private accumulator: number = 0;

  constructor(
    updateCallback: (deltaTime: number) => void,
    renderCallback: () => void
  ) {
    this.updateCallback = updateCallback;
    this.renderCallback = renderCallback;
  }

  setErrorHandler(handler: (error: { message: string; stack: string }) => void): void {
    this.onError = handler;
  }

  start(): void {
    if (this.animationFrameId !== null) return;

    this.lastTime = performance.now();
    this.fpsTime = this.lastTime;
    this.errorState = null;
    this.loop(this.lastTime);
  }

  stop(): void {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  hasError(): boolean {
    return this.errorState !== null;
  }

  getError(): { message: string; stack: string } | null {
    return this.errorState;
  }

  clearError(): void {
    this.errorState = null;
  }

  private loop = (currentTime: number): void => {
    this.animationFrameId = requestAnimationFrame(this.loop);

    // Skip processing if in error state
    if (this.errorState) return;

    // Calculate delta time in seconds
    const rawDeltaTime = (currentTime - this.lastTime) / 1000;
    this.lastTime = currentTime;

    // Cap delta time to prevent spiral of death
    this.deltaTime = Math.min(rawDeltaTime, 0.25);

    try {
      // Fixed time step update (for physics/game logic)
      this.accumulator += this.deltaTime;

      // Cap iterations to prevent spiral-of-death: at low FPS the accumulator
      // can queue many updates per frame, making each frame even slower.
      // Max 3 iterations keeps physics within budget when rendering is the bottleneck.
      let maxIterations = 3;
      const screenShake = getScreenShake();
      while (this.accumulator >= this.fixedDeltaTime && maxIterations-- > 0) {
        // Hit-stop: freeze game but tick down the hit-stop timer
        if (screenShake.isHitStopped()) {
          screenShake.tickHitStop(this.fixedDeltaTime);
          this.accumulator -= this.fixedDeltaTime;
          continue;
        }
        this.updateCallback(this.fixedDeltaTime);
        this.accumulator -= this.fixedDeltaTime;
      }
      // Clamp leftover accumulator to prevent it from growing unboundedly
      if (this.accumulator > this.fixedDeltaTime) {
        this.accumulator = this.fixedDeltaTime;
      }

      // Render
      this.renderCallback();
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      this.errorState = {
        message: error.message,
        stack: error.stack || 'No stack trace',
      };
      if (this.onError) {
        this.onError(this.errorState);
      }
    }

    // FPS calculation
    this.frameCount++;
    if (currentTime - this.fpsTime >= 1000) {
      this.fps = this.frameCount;
      this.frameCount = 0;
      this.fpsTime = currentTime;
    }
  };

  getFPS(): number {
    return this.fps;
  }

  getDeltaTime(): number {
    return this.deltaTime;
  }

  getFixedDeltaTime(): number {
    return this.fixedDeltaTime;
  }
}
